import React, { useState, useEffect } from 'react';
import { TriBetLink } from '../components/TriBetLink';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Safe response reader: avoids "body stream already read" caused by the
// session recorder intercepting fetch responses. Reads via arrayBuffer.
async function safeReadResponse(response) {
  try {
    const buffer = await response.arrayBuffer();
    const text = new TextDecoder().decode(buffer);
    return text ? JSON.parse(text) : {};
  } catch (e) {
    if (!response.ok) {
      return { detail: `Request failed with status ${response.status}` };
    }
    throw new Error('Unable to read server response.');
  }
}

const money = (n) => {
  const v = Number(n || 0);
  return `$${v.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

export default function PricingPage() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(null); // which plan_id is loading
  const [error, setError] = useState('');
  const [config, setConfig] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/subscriptions/pricing`);
        const data = await safeReadResponse(res);
        setConfig(data);
      } catch {
        setConfig(null);
      }
    })();
  }, []);

  // Contextual "your access expired" message for trial/subscription users
  const expiredNotice = (() => {
    if (!user || user.role === 'admin') return null;
    const fmt = (d) =>
      new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const isPast = (d) => {
      if (!d) return false;
      return new Date(new Date(d).toDateString()) < new Date(new Date().toDateString());
    };
    if (user.subscription_status === 'trial' && isPast(user.trial_ends_at)) {
      return `Your free trial ended on ${fmt(user.trial_ends_at)}. Subscribe below to regain full access.`;
    }
    if (user.subscription_status === 'active' && isPast(user.subscription_ends_at)) {
      return `Your subscription expired on ${fmt(user.subscription_ends_at)}. Renew below to regain full access.`;
    }
    return null;
  })();

  // Trial-eligible only while still inside the signup trial (not yet ended).
  const trialEligible = !!user
    && user.subscription_status === 'trial'
    && user.trial_ends_at
    && new Date(user.trial_ends_at) > new Date();

  const handleSubscribe = async (planId) => {
    setError('');
    setLoading(planId);
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ plan_id: planId, origin_url: window.location.origin }),
      });
      const data = await safeReadResponse(response);
      if (!response.ok) {
        throw new Error((data && data.detail) || 'Failed to create checkout session');
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.message);
      setLoading(null);
    }
  };

  const plans = config?.plans || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-white">
            IntelliTix<span className="text-emerald-400">®</span>
          </Link>
          <div className="flex items-center gap-4">
            {user && (
              <>
                <TriBetLink />
                <span className="text-slate-400 text-sm">{user.email}</span>
                <button onClick={logout} className="text-sm text-slate-400 hover:text-white transition" data-testid="pricing-logout">
                  Logout
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Pricing Content */}
      <main className="max-w-6xl mx-auto px-4 py-16">
        {config?.free_access_day && (
          <div
            className="max-w-4xl mx-auto mb-10 bg-emerald-500/15 border border-emerald-500/50 text-emerald-300 px-5 py-4 rounded-xl flex items-center gap-3"
            data-testid="pricing-free-access-banner"
          >
            <svg className="w-6 h-6 flex-shrink-0 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            <span className="text-sm md:text-base font-medium">
              🎉 It's a Free Access day! IntelliTix &amp; IntelliTix Pro are free today for all members —
              no subscription needed. Subscribe below for full access every day.
            </span>
          </div>
        )}
        {expiredNotice && (
          <div
            className="max-w-4xl mx-auto mb-10 bg-amber-500/10 border border-amber-500/40 text-amber-300 px-5 py-4 rounded-xl flex items-center gap-3"
            data-testid="trial-expired-banner"
          >
            <svg className="w-6 h-6 flex-shrink-0 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <span className="text-sm md:text-base font-medium">{expiredNotice}</span>
          </div>
        )}

        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4" data-testid="pricing-headline">
            {config?.headline || 'Choose Your Plan'}
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            {config?.subheadline || ''}
          </p>
          <p className="text-emerald-400 text-sm font-medium mt-3" data-testid="pricing-free-days-note">
            Members get IntelliTix &amp; IntelliTix Pro free every Monday &amp; Tuesday (ET).
          </p>
        </div>

        {error && (
          <div className="max-w-4xl mx-auto mb-8 bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-center" data-testid="pricing-error">
            {error}
          </div>
        )}

        {/* Pricing Cards */}
        <div className={plans.length === 1 ? 'max-w-md mx-auto' : 'grid md:grid-cols-2 gap-8 max-w-4xl mx-auto'}>
          {plans.map((plan) => (
            <div
              key={plan.key}
              className={`bg-slate-800/50 backdrop-blur-sm border rounded-2xl p-8 relative ${plan.coming_soon ? 'border-slate-600 opacity-90' : 'border-slate-700'}`}
              data-testid={`plan-card-${plan.key}`}
            >
              {plan.coming_soon && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-amber-500 text-white text-xs font-semibold px-3 py-1 rounded-full animate-pulse">
                    COMING SOON
                  </span>
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-xl font-semibold text-white mb-2">{plan.name}</h3>
                <p className="text-slate-400">{plan.description}</p>
              </div>

              <div className="mb-6">
                <span className={`text-5xl font-bold ${plan.coming_soon ? 'text-slate-400' : 'text-white'}`}>
                  {money(plan.monthly_amount)}
                </span>
                <span className={plan.coming_soon ? 'text-slate-500' : 'text-slate-400'}>/month</span>
                {plan.savings_label && (
                  <p className={`text-sm mt-1 ${plan.coming_soon ? 'text-slate-500' : 'text-emerald-400'}`}>
                    {plan.savings_label}
                  </p>
                )}
              </div>

              <ul className="space-y-3 mb-8">
                {(plan.features || []).map((feature, i) => (
                  <li key={i} className={`flex items-center ${plan.coming_soon ? 'text-slate-400' : 'text-slate-300'}`}>
                    <svg className={`w-5 h-5 mr-3 flex-shrink-0 ${plan.coming_soon ? 'text-slate-500' : 'text-emerald-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>

              <div className="space-y-3">
                {plan.coming_soon ? (
                  <button
                    disabled
                    className="w-full py-3 px-4 bg-slate-700 cursor-not-allowed text-slate-400 font-semibold rounded-lg flex items-center justify-center gap-2"
                    data-testid={`subscribe-${plan.key}-monthly`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Launching Soon
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => handleSubscribe(plan.monthly_plan_id)}
                      disabled={loading !== null}
                      className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition duration-200 flex items-center justify-center"
                      data-testid={`subscribe-${plan.key}-monthly`}
                    >
                      {loading === plan.monthly_plan_id ? (
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : (
                        `${trialEligible ? 'Start Free Trial' : 'Monthly Plan'} - ${money(plan.monthly_amount)}/mo`
                      )}
                    </button>
                    {plan.daily_plan_id && (
                      <button
                        onClick={() => handleSubscribe(plan.daily_plan_id)}
                        disabled={loading !== null}
                        className="w-full py-2.5 px-4 bg-orange-500 hover:bg-orange-400 disabled:bg-orange-800 disabled:cursor-not-allowed text-white font-bold rounded-lg transition duration-200 shadow-md shadow-orange-500/30 border border-orange-400/40"
                        data-testid={`subscribe-${plan.key}-daily`}
                      >
                        {loading === plan.daily_plan_id ? 'Processing...' : `Day Pass — ${money(plan.daily_amount)} / 24 hours`}
                      </button>
                    )}
                    {plan.yearly_plan_id && (
                      <button
                        onClick={() => handleSubscribe(plan.yearly_plan_id)}
                        disabled={loading !== null}
                        className="w-full py-2 px-4 border border-slate-600 hover:border-emerald-500 text-slate-300 hover:text-white font-medium rounded-lg transition duration-200"
                        data-testid={`subscribe-${plan.key}-yearly`}
                      >
                        {loading === plan.yearly_plan_id ? 'Processing...' : `Annual Plan - ${money(plan.yearly_amount)}/yr`}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-16 text-center">
          <p className="text-slate-400">{config?.footer_note || ''}</p>
          <p className="text-slate-500 text-sm mt-2">{config?.footer_contact || ''}</p>
        </div>
      </main>
    </div>
  );
}
